import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';

import { useLoaderStore } from '@/stores/useLoaderStore';
import { useUserStore } from '@/stores/useUserStore';
import { fetchGetJobSearchByPostcode } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

export interface PostCodeSearchViewDataComposable {
  isFetchingPostCodeJobs: Readonly<Ref<boolean>>;
  postCodeJobs: Readonly<
    Ref<ReadonlyDeep<openapi.JobSearchJobByPostcodeContentOpenapi> | undefined>
  >;
  refreshPostCodeJobs: () => Promise<void>;
}

export default function usePostCodeSearchView(
  querySuburbId: Readonly<Ref<number | undefined>>,
  queryPostCode: Readonly<Ref<number | undefined>>,
  queryDistance: Readonly<Ref<number | undefined>>,
  queryDates: Readonly<Ref<[Date, Date] | undefined>>,
): PostCodeSearchViewDataComposable {
  const route = useRoute();
  const userStore = useUserStore();

  const loaderStore = useLoaderStore();

  let postCodeJobsController: AbortController | undefined;
  const userTimeZone = computed(() => userStore.user.timezone);

  const postCodeJobsFetchCounter = ref(0);
  const isFetchingPostCodeJobs = computed(
    () => !!postCodeJobsFetchCounter.value,
  );
  const postCodeJobs =
    ref<ReadonlyDeep<openapi.JobSearchJobByPostcodeContentOpenapi>>();

  function clear(): void {
    if (postCodeJobsController) postCodeJobsController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchPostCodeJobs(): Promise<
    ReadonlyDeep<openapi.JobSearchJobByPostcodeContentOpenapi> | undefined
  > {
    if (postCodeJobsController) postCodeJobsController.abort();
    const suburbId = querySuburbId.value;
    const postCode = queryPostCode.value;
    const distance = queryDistance.value;
    const dates = queryDates.value;
    if (
      !dates ||
      !suburbId?.toString() ||
      !postCode?.toString() ||
      !distance?.toString()
    ) {
      return;
    }
    postCodeJobsController = new AbortController();
    return await fetchGetJobSearchByPostcode(
      userTimeZone.value,
      {
        dates,
        suburbId,
        postCode,
        distance,
      },
      {
        signal: postCodeJobsController.signal,
      },
    )
      .then(function (data) {
        return data;
      })
      .catch(function (err: unknown) {
        if (!isCancel(err)) {
          throw err;
        }
        return undefined;
      });
  }

  async function refreshPostCodeJobs(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    postCodeJobsFetchCounter.value++;
    try {
      postCodeJobs.value = await _fetchPostCodeJobs();
    } finally {
      postCodeJobsFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  watch(
    [querySuburbId, queryPostCode, queryDistance, queryDates, route],
    async function (newValues, oldValues) {
      if (route.name !== routeNames.SEARCH_POSTCODE) return;
      await refreshPostCodeJobs();
    },
    { immediate: true },
  );

  return {
    isFetchingPostCodeJobs,
    postCodeJobs,
    refreshPostCodeJobs,
  };
}
